import { PaymentMethodsEmptyState } from '@/features/billing/components/PaymentMethodsEmptyState'
import { BillingMethodList } from '@/features/billing/components/BillingMethodList'
import { BillingMethodListItemContainer } from '@/features/billing/components/BillingMethodList/BillingMethodListItemContainer'
import { DeleteBillingMethodDialogContainer } from '@/features/billing/payment-methods/DeleteBillingMethodDialog/DeleteBillingMethodDialogContainer'
import { ContentError } from '@/components/ContentError'
import { ContentLoading } from '@/components/ContentLoading'
import {
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
} from '@/components/Dropdown'
import { ListHeader } from '@/components/ListHeader'
import { ContentEmpty } from '@/components/ContentLayout'
import { observer } from 'mobx-react-lite'
import { useFeatures } from '@/contexts/root-store-context'
import { type ProfilePaymentMethodsViewModel } from './ProfilePaymentMethodsViewModel'
import { Icon } from '@/components/Icon'

export interface ProfilePaymentMethodsContainerProps {
  vm: ProfilePaymentMethodsViewModel
}

export const ProfilePaymentMethodsContainer = observer(
  function ProfilePaymentMethodsContainer({
    vm,
  }: ProfilePaymentMethodsContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))
    return (
      <>
        {vm.fetch.match({
          pending: () => <ContentLoading />,
          rejected: (err) => <ContentError error={err} />,
          resolved: () => (
            <>
              {vm.methods.items.length === 0 ? (
                <ContentEmpty>
                  <PaymentMethodsEmptyState
                    canAddPaymentMethod={true}
                    isAddPending={vm.addPaymentMethod.pending}
                    onNewPaymentMethod={vm.addPaymentMethod}
                    corporateMode={shellInfo.corporateMode}
                  />
                </ContentEmpty>
              ) : (
                <>
                  <ListHeader count={vm.methods.items.length}>
                    Payment Methods
                  </ListHeader>
                  <BillingMethodList
                    isAddDisabled={vm.disabled}
                    onAdd={vm.addPaymentMethod}
                  >
                    {vm.methods.items.map((methodVm) => (
                      <BillingMethodListItemContainer
                        vm={methodVm}
                        isDisabled={vm.disabled}
                        onDelete={
                          vm.canDeletePaymentMethods
                            ? methodVm.delete
                            : undefined
                        }
                        key={methodVm.id}
                        dropdownActions={
                          <>
                            <DropdownItem
                              isDisabled={methodVm.isDefault}
                              onPress={methodVm.setAsDefault}
                              icon={<Icon name="default-payment" />}
                            >
                              <DropdownItemTitle>
                                Set as Current
                              </DropdownItemTitle>
                              <DropdownItemDescription>
                                {methodVm.isDefault ? (
                                  <>
                                    This payment method is already the default.
                                  </>
                                ) : (
                                  <>
                                    Future payments will be charged to this
                                    payment method.
                                  </>
                                )}
                              </DropdownItemDescription>
                            </DropdownItem>
                          </>
                        }
                      />
                    ))}
                  </BillingMethodList>
                </>
              )}
            </>
          ),
        })}
        <DeleteBillingMethodDialogContainer vm={vm.deleteDialogVm} />
      </>
    )
  },
)
